// Hosted media playback: the media dock's own element is the ONLY runtime for
// a track. While the post that started it is on screen the dock sits exactly
// over that post's media frame; when the post scrolls or slides away the same
// element glides to the dock corner. Nothing is preloaded twice or handed off,
// so navigation cannot interrupt playback.
//
// On for everyone (the `hosted_media_playback` feature no longer gates it). QA
// and device checks can force it on or off before the app boots with
// `window.__KNOV_HOSTED_MEDIA_PLAYBACK__` or the localStorage key below.

export const HOSTED_MEDIA_PLAYBACK_FEATURE = 'hosted_media_playback'
export const HOSTED_MEDIA_PLAYBACK_STORAGE_KEY = 'treechat:hosted-media-playback'

type HostedMediaPlaybackWindow = Window & {
    __KNOV_HOSTED_MEDIA_PLAYBACK__?: boolean
}

function readOverride(): boolean | null {
    if (typeof window === 'undefined') return null

    const override = (window as HostedMediaPlaybackWindow).__KNOV_HOSTED_MEDIA_PLAYBACK__
    if (typeof override === 'boolean') return override

    try {
        const stored = window.localStorage?.getItem(HOSTED_MEDIA_PLAYBACK_STORAGE_KEY)
        if (stored === '1' || stored === 'true') return true
        if (stored === '0' || stored === 'false') return false
    } catch {
        // Storage can be unavailable (private mode, sandboxed previews).
    }

    return null
}

// On for everyone. The override still lets QA and device checks force it off.
export function hostedMediaPlaybackEnabled(): boolean {
    const override = readOverride()
    if (override != null) return override
    return true
}

// YouTube is the kind that gains the most — its iframe cannot be moved in the
// DOM without reloading — but native media has the same defect in a milder
// form (a second element, a re-download and a re-seek on every navigation), so
// both are hosted under the one feature.
export function hostedYouTubePlaybackEnabled(): boolean {
    return hostedMediaPlaybackEnabled()
}

export function hostedNativeMediaEnabled(): boolean {
    return hostedMediaPlaybackEnabled()
}

/**
 * How a hosted runtime is presented while its post is on screen.
 *
 * `visual` — the runtime has a picture (YouTube, video), so the dock is
 * positioned over the post's media frame and tracks it.
 * `silent` — the runtime is invisible (audio), so there is nothing to place:
 * the dock stays hidden and the post keeps drawing its own transport UI. The
 * placement exists only so the dock knows when the post has left the screen
 * and it should reveal itself in the corner.
 */
export type HostedPlacementKind = 'visual' | 'silent'
